import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    CardTitle,
    Row,
    Col,
    Button,
    Container,
    Table,
    Badge,
    Alert,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Label,
    FormGroup
} from "reactstrap";
import classnames from "classnames";
import Select from "react-select";
import { MessageSquare, Send, RefreshCw, Trash2, Smartphone } from "lucide-react";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { sendWhatsAppTest, getWhatsAppReplies, clearWhatsAppReplies } from "../../components/Api/GmsWhatsAppWebhook";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";

const GmsWhatsAppTest = () => {
    const [activeTab, setActiveTab] = useState("1");
    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: "", message: "" });
    const [lastRefresh, setLastRefresh] = useState(new Date().toLocaleTimeString());

    const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);

    const [businesses, setBusinesses] = useState([]);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [businessesLoading, setBusinessesLoading] = useState(false);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    const handleCustomerSearch = (inputValue, { action }) => {
        if (action !== 'input-change') return;
        
        const searchText = inputValue ? inputValue.trim() : '';

        if (searchTimeout) clearTimeout(searchTimeout);

        if (searchText.length < 3) {
            setCustomerSearchOptions([]); // Clear options if less than 3 chars
            return;
        }

        setSearchTimeout(
            setTimeout(async () => {
                try {
                    setCustomerSearchLoading(true);
                    const result = await searchCustomers(searchText, 1, 50);
                    const customersList = result?.items || result?.data || (Array.isArray(result) ? result : []);

                    if (customersList) {
                        const options = customersList.map(c => ({
                            value: c.customer_Id,
                            label: `${c.customer_NameEn} - ${c.customer_ContactNo}`,
                            phone: c.customer_ContactNo,
                            customer: c
                        }));
                        setCustomerSearchOptions(options);
                    }
                } catch (error) {
                    console.error("Error searching customers:", error);
                } finally {
                    setCustomerSearchLoading(false);
                }
            }, 500)
        );
    };

    const loadReplies = async () => {
        try {
            setLoading(true);
            const data = await getWhatsAppReplies();
            setReplies(data);
            setLastRefresh(new Date().toLocaleTimeString());
        } catch (error) {
            console.error("Failed to load replies", error);
        } finally {
            setLoading(false);
        }
    };

    const loadBusinesses = async () => {
        try {
            setBusinessesLoading(true);
            const data = await getBusinesses({ business_Active: true });
            if (data && Array.isArray(data)) {
                const options = data.map(b => ({
                    value: b.business_Id,
                    label: `${b.business_NameEn} (${b.business_Code})`
                }));
                setBusinesses(options);
            }
        } catch (error) {
            console.error("Error loading businesses:", error);
        } finally {
            setBusinessesLoading(false);
        }
    };

    const handleSendTest = async () => {
        if (!selectedCustomerOption || !selectedCustomerOption.phone) {
            setStatus({ type: "warning", message: "Please select a customer with a valid contact number." });
            return;
        }

        try {
            setStatus({ type: "info", message: `Sending message to ${selectedCustomerOption.label}...` });
            const result = await sendWhatsAppTest(
                selectedCustomerOption.phone, 
                "hello_world", 
                selectedBusiness ? selectedBusiness.value : null,
                selectedCustomerOption.value
            );
            if (result.success) {
                setStatus({ type: "success", message: `Successfully sent 'hello_world' template to ${selectedCustomerOption.label}` });
            } else {
                setStatus({ type: "danger", message: "Failed: " + result.message });
            }
        } catch (error) {
            setStatus({ type: "danger", message: "Error sending message" });
        }
    };

    useEffect(() => {
        loadReplies();
        loadBusinesses();
    }, []);

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="GMS" breadcrumbItem="WhatsApp Integration" />

                <Card className="shadow-sm">
                    <CardBody>
                        <Nav tabs className="nav-tabs-custom mb-4">
                            <NavItem>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={classnames({ active: activeTab === "1" })}
                                    onClick={() => { toggleTab("1"); }}
                                >
                                    <Send className="me-2" size={16} /> Send Message
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    style={{ cursor: "pointer" }}
                                    className={classnames({ active: activeTab === "2" })}
                                    onClick={() => { toggleTab("2"); }}
                                >
                                    <MessageSquare className="me-2" size={16} /> Message Log
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="1">
                                <Row className="justify-content-center">
                                    <Col lg={6}>
                                        <Card className="border shadow-none">
                                            <CardBody>
                                                <CardTitle className="h4 mb-4 text-center">Send WhatsApp Template</CardTitle>
                                                
                                                <FormGroup className="mb-3">
                                                    <Label>1. Select Business (Credentials Source)</Label>
                                                    <Select
                                                        options={businesses}
                                                        value={selectedBusiness}
                                                        onChange={(option) => {
                                                            setSelectedBusiness(option);
                                                            setStatus({ type: "", message: "" });
                                                        }}
                                                        placeholder="Choose which business to use..."
                                                        isLoading={businessesLoading}
                                                        isClearable
                                                        classNamePrefix="select2-selection"
                                                    />
                                                    {selectedBusiness && (
                                                        <small className="text-info mt-1 d-block">
                                                            <i className="bx bx-info-circle me-1"></i>
                                                            This will use the 5 WhatsApp credentials configured for <strong>{selectedBusiness.label}</strong>.
                                                        </small>
                                                    )}
                                                </FormGroup>

                                                <FormGroup className="mb-4">
                                                    <Label>2. Select Recipient Customer</Label>
                                                    <Select
                                                        options={customerSearchOptions}
                                                        value={selectedCustomerOption}
                                                        onInputChange={handleCustomerSearch}
                                                        onChange={(option) => {
                                                            setSelectedCustomerOption(option);
                                                            setStatus({ type: "", message: "" });
                                                        }}
                                                        placeholder="Search Customer by Name, Mobile..."
                                                        isLoading={customerSearchLoading}
                                                        isClearable
                                                        classNamePrefix="select2-selection"
                                                        disabled={!selectedBusiness}
                                                    />
                                                </FormGroup>

                                                {selectedCustomerOption && (
                                                    <div className="p-3 bg-light rounded border text-center mb-4">
                                                        <div className="d-flex align-items-center justify-content-center mb-2 text-primary">
                                                            <Smartphone className="me-2" size={18} />
                                                            <strong>Recipient Phone</strong>
                                                        </div>
                                                        <code className="h5">{selectedCustomerOption.phone}</code>
                                                    </div>
                                                )}

                                                <div className="d-grid gap-3">
                                                    <Button
                                                        color="primary"
                                                        size="lg"
                                                        className="d-flex align-items-center justify-content-center"
                                                        onClick={handleSendTest}
                                                        disabled={!selectedCustomerOption}
                                                    >
                                                        <Send className="me-2" size={18} /> Send "Hello World" Template
                                                    </Button>
                                                </div>

                                                {status.message && (
                                                    <Alert color={status.type} className="mt-4 mb-0">
                                                        {status.message}
                                                    </Alert>
                                                )}
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            </TabPane>

                            <TabPane tabId="2">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h5 className="mb-0">Recent Messages
                                        <small className="text-muted ms-2">(Last Auto-refresh: {lastRefresh})</small>
                                    </h5>
                                    <Button
                                        color="soft-info"
                                        outline
                                        className="d-flex align-items-center"
                                        onClick={loadReplies}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={16} className={`me-2 ${loading ? 'spin' : ''}`} />
                                        {loading ? "Refreshing..." : "Refresh Logs"}
                                    </Button>
                                </div>

                                <div className="table-responsive" style={{ minHeight: "400px" }}>
                                    <Table className="table-centered table-nowrap mb-0 table-hover border">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Time</th>
                                                <th>From Number</th>
                                                <th>Customer</th>
                                                <th>Message</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {replies.length > 0 ? (
                                                replies.map((msg, idx) => (
                                                    <tr key={idx}>
                                                        <td>{new Date(msg.receivedAt || msg.ReceivedAt).toLocaleString()}</td>
                                                        <td><Badge color="soft-primary" className="font-size-12 px-2 py-1">{msg.fromNumber || msg.FromNumber}</Badge></td>
                                                        <td>
                                                            {(msg.customerName || msg.CustomerName) ? (
                                                                <span className="fw-semibold text-primary">{msg.customerName || msg.CustomerName}</span>
                                                            ) : (
                                                                <span className="text-muted fst-italic">Unknown</span>
                                                            )}
                                                        </td>
                                                        <td className="text-wrap" style={{ maxWidth: "400px" }}>{msg.messageBody || msg.MessageBody}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-5">
                                                        <div className="text-muted">No messages found</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </TabPane>
                        </TabContent>
                    </CardBody>
                </Card>
            </Container>

            <style>{`
        .spin { animation: rotation 2s infinite linear; }
        @keyframes rotation { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
        .bg-soft-warning { background-color: rgba(241, 180, 76, 0.1); }
        .bg-soft-info { background-color: rgba(80, 165, 241, 0.1); }
        .bg-soft-danger { background-color: rgba(244, 106, 106, 0.1); }
        .nav-tabs-custom .nav-item .nav-link {
            border: none;
            color: #495057;
            font-weight: 500;
            padding: 1rem 1.5rem;
            position: relative;
        }
        .nav-tabs-custom .nav-item .nav-link.active {
            color: #556ee6;
            background-color: transparent;
        }
        .nav-tabs-custom .nav-item .nav-link.active::after {
            content: "";
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            height: 2px;
            background-color: #556ee6;
        }
      `}</style>
        </div>
    );
};

export default GmsWhatsAppTest;
